/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'
import { InputSelect } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'

import Actions from '../../../../actions'
import Operations from '../../../../operations'
import { getObjectDiff } from '../../../../common/utils'
import Selectors from '../../../../selectors'
import { history } from '../../../../store'
import { withRouter } from '../../../../withRouter'
import { showToast } from '../../../../common/utils'

import Form from '../../../../components/forms'
import { Spinner } from '../../../../components/generic'
import {
  RiskAssessmentsSection,
  CarePlanDocuments
} from '../../../../components/carePlans'
import SignatureSection from '../../components/CarePlanSignatures/CarePlanSignatures'

import Header, {
  ICarePlanForm
} from './components/CarePlanReviewHeader/CarePlanReviewHeader'
import Section from './components/CarePlanReviewSection/CarePlanReviewSection'
import Footer from './components/CarePlanReviewFooter/CarePlanReviewFooter'
import NextReview from './components/CarePlanReviewNextReview/CarePlanReviewNextReview'
import styles from './CarePlanReiviewForm.styles'
// we need full path here in order to make jest.mock work
import {
  Carer,
  Document,
  CarePlan,
  Resident,
  LogCategoryItem,
  RiskAssessment,
  Log,
  Home
} from '@lmc/ts-common'
import { Form as AntdForm } from 'antd'
import { ProfileNeedOption } from 'screens/Residents/screens/ResidentNeedsScreen/ResidentNeedsScreen'
import useSkillsAndNeedsTemplate, {
  NeedsAndSkills
} from 'hooks/useSkillsAndNeedsTemplate'
import CarePlanGoalsListInput from '../CarePlanViewScreen/components/CarePlansGoalsListInput/CarePlanGoalsListInput'
import useGoalsAccess from 'components/AccessControl/features/useGoalsAccess'
import { CarePlanSectionContainer } from '../../components/CarePlanSections'
import { CAREPLAN_ADL_PBS_ID } from '@lmc/common/types'
import {
  getFormSectionsInitialValue,
  getSectionsToSave,
  getDefaultPBSSections
} from './utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import FluidTrackingSection from './components/FluidTrackingSection/FluidTrackingSection'
import ConsentSection, { ConsentFormData } from './components/ConsentSection'
import { carePlansConstants } from 'locale/carePlans'
import { NAMESPACE } from 'locale'
import { useQueryClient } from 'react-query'

const EATING_AND_DRINKING_ADL = '5d3ef8e7610ca2e6713c60f0'

type SetFormField = {
  key: string
  value: string | number | string[] | boolean
}

type UpdateItem = {
  url: string
  json: CarePlan
  onComplete: () => void
  onError: (e: Error) => void
}

interface CarePlanFields extends CarePlan {
  fluidTrackingEnabled: boolean
  fluidTrackingIntakeTarget: number
  consentForms?: ConsentFormData[]
  consentFormIds?: string[]
  populatedDocuments?: Document[]
  populatedRiskAssessments?: RiskAssessment[]
}

interface CarePlanFormType {
  carers: Carer[]
  carePlanNominative: string
  deleteDocument: (id: string, cb: () => void) => void
  documents: Document
  errors?: string[]
  initial: CarePlan
  isSubmitting: boolean
  fields: CarePlanFields
  openDocumentModal: (doc: { type: string; document?: Document }) => void
  openRiskAssessmentModal: (doc: RiskAssessment) => void
  params: { id: string; care_plan_id: string }
  riskAssessments?: RiskAssessment[]
  resident: Resident
  router: any
  setFormField: (a: SetFormField) => void
  setLoadedListItem: (update: { listId: string; item: any }) => void
  signatureLogs?: Log[]
  signatureItem?: LogCategoryItem
  updateItem: (update: UpdateItem) => void
  profileNeedOptions: ProfileNeedOption[]
  features: Home
  fetchNeedsOptions: () => void
  goals: string[]
  removeForm: () => void
  adls: any[]
}

export const CarePlanReviewForm = ({
  carers,
  carePlanNominative,
  deleteDocument,
  documents,
  errors,
  initial,
  isSubmitting,
  fields,
  openDocumentModal,
  openRiskAssessmentModal,
  params: { id, care_plan_id: carePlanId },
  riskAssessments,
  resident,
  router,
  setFormField,
  setLoadedListItem,
  signatureLogs,
  signatureItem,
  updateItem,
  profileNeedOptions,
  features,
  fetchNeedsOptions,
  removeForm,
  adls
}: CarePlanFormType): React.ReactElement => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)

  const queryClient = useQueryClient()

  const hasGoals = useGoalsAccess()

  const [success, setSuccess] = useState(false)
  const [showFluidTrackingValidation, setShowFluidTrackingValidation] =
    useState(false)

  // Prevent accidentally navigating away, loosing unsaved work
  const isDirty = !_.isEqual(fields, initial) // Is this too heavy?

  const flags = useFeatureFlags({
    flagNames: ['pbs', 'fluid_tracking_intake_targets', 'informed_consent']
  })?.data

  const pbsFlagEnabled = flags.pbs.enabled
  const fluiTrackingFlagEnabled = flags.fluid_tracking_intake_targets.enabled
  const informedConsentFlagEnabled = flags.informed_consent?.enabled || false

  const isPBSAssessment =
    pbsFlagEnabled && fields?.carePlanAdl?.id === CAREPLAN_ADL_PBS_ID

  const url = `/residents/${id}/care-plans/${carePlanId}`

  const handleCancel = () => history.push(url)

  const [form] = AntdForm.useForm()

  const [formPBS] = AntdForm.useForm()

  const canNavigate = !isDirty || success

  const displayFluidTracking =
    fluiTrackingFlagEnabled &&
    fields?.carePlanAdl?.id?.toString() === EATING_AND_DRINKING_ADL

  const submitSectionsForm = async () => {
    let formResult
    try {
      await formPBS.validateFields()
      formResult = formPBS.getFieldsValue()
    } catch (e) {
      formResult = false
    }
    return formResult
  }

  const { textNode, linkNode } = useSkillsAndNeedsTemplate({
    type: NeedsAndSkills.needs,
    elements: []
  })

  const handleSubmit = () => {
    setShowFluidTrackingValidation(false)

    const fluidTrackingEnabled =
      fields.fluidTrackingEnabled ?? fluidTrackingDefaults.enabled
    const fluidTrackingIntakeTarget =
      fields.fluidTrackingIntakeTarget ?? fluidTrackingDefaults.intakeTarget

    if (fluiTrackingFlagEnabled && fluidTrackingEnabled) {
      if (
        !fluidTrackingIntakeTarget ||
        fluidTrackingIntakeTarget.toString() === ''
      ) {
        showToast({
          title: t(carePlansConstants.ERROR_FLUID_TARGET_CAN_NOT_BE_EMPTY),
          type: 'warning'
        })
        setShowFluidTrackingValidation(true)
        return
      }

      if (fluidTrackingIntakeTarget > 9999 || fluidTrackingIntakeTarget === 0) {
        showToast({
          title: t(carePlansConstants.ERROR_CORRECT_AMOUNT),
          type: 'warning'
        })
        setShowFluidTrackingValidation(true)
        return
      }
    }

    if (isDirty && !fields.noFurtherSupportRequired && !fields.nextReviewDate) {
      showToast({
        title: 'Next review date needs to be specified',
        type: 'warning'
      })
      return
    }

    if (isDirty || formPBS?.isFieldsTouched()) {
      const changes = getObjectDiff(fields, initial)

      // On submit, reviewComment is set to newComment,
      // and newComment is set to null. This makes sure
      // that if no comment is entered on review, the
      // default value of comment is null.
      //
      // If newComment is undefined, reviewComment is still set to null.
      changes.reviewComment = changes.newComment ? changes.newComment : null
      delete changes.newComment
      delete changes.fluidTrackingEnabled
      delete changes.fluidTrackingIntakeTarget

      setSuccess(true)

      const url = `list/care-plans/${fields.id}`

      const fluidTrackingEnabled = fields.noFurtherSupportRequired
        ? false
        : (fields.fluidTrackingEnabled ?? fluidTrackingDefaults.enabled)

      const intakeTargetValue =
        fields.fluidTrackingIntakeTarget || fluidTrackingDefaults.intakeTarget
      const baseJson = {
        ...changes,
        carePlanId: fields.carePlanId,
        reviewedAssessments: undefined,
        // @ts-ignore
        seenBy: null,
        ...(displayFluidTracking && {
          fluidTracking: {
            enabled: fluidTrackingEnabled,
            intakeTarget: intakeTargetValue ? intakeTargetValue : undefined
          }
        })
      }
      if (isPBSAssessment) {
        const sectionsFormResult = submitSectionsForm()

        sectionsFormResult.then((sections) => {
          const sectionsToSave = getSectionsToSave(pbsSectionsForForm, sections)

          if (sections) {
            updateItem({
              url,
              json: {
                ...baseJson,
                sections: sectionsToSave
              } as unknown as CarePlan,
              onComplete: handleComplete,
              onError: (e: Error) => {
                // eslint-disable-next-line no-console
                console.error(e)
              }
            })
          }
        })
        return
      }

      updateItem({
        url: `list/care-plans/${fields.id}`,
        json: baseJson as unknown as CarePlan,
        onComplete: handleComplete,
        onError: (e: Error) => {
          // eslint-disable-next-line no-console
          console.error(e)
        }
      })
    } else {
      handleCancel()
    }
  }

  const handleComplete = () => {
    showToast({ title: 'Reviewed a Care Plan' })

    queryClient.invalidateQueries(['care-plan-fluid-intake-targets', id])
    queryClient.invalidateQueries(['care-plan-fluid-intake', id])
    queryClient.invalidateQueries(['care-plans', carePlanId, 'populated'])

    removeForm()
    history.push(`/residents/${id}/care-plans/${carePlanId}`)
  }

  const handleDeleteDocument = (documentId: string) => {
    showToast({ type: 'info', title: 'Deleted a Care Plan Document' })
    deleteDocument(documentId, () => {
      setFormField({
        key: 'documents',
        value: _.without(fields.documents || [], documentId) as
          | string
          | number
          | boolean
          | string[]
      })
      // Ensure any view using the populated care plan sees the deletion immediately
      queryClient.invalidateQueries(['care-plans', carePlanId, 'populated'])
    })
  }

  useEffect(() => {
    form.setFieldsValue({ skillNeeds: fields?.skillNeeds })
  }, [fields?.skillNeeds])

  useEffect(() => {
    if (features?.features?.skillsAndNeeds?.enabled) {
      fetchNeedsOptions()
    }
  }, [])

  useEffect(() => {
    if (!isPBSAssessment) return
    if (isDirty) return

    formPBS.setFieldsValue(getFormSectionsInitialValue(fields))
  }, [isPBSAssessment, isDirty, fields?.sections])

  const hasInitialPBSSections = fields?.sections?.length

  // Derive PBS ADL sections either from the care plan's ADL snapshot or from the global ADL list
  const pbsAdlSectionsFromCarePlan = (fields as any)?.carePlanAdl?.sections
  const pbsAdlFromGlobal = adls?.find(
    (adl) => adl?.id === (fields as any)?.carePlanAdl?.id
  )
  const pbsAdlSectionsFromGlobal = (pbsAdlFromGlobal as any)?.sections

  const pbsAdlSections =
    (pbsAdlSectionsFromCarePlan && pbsAdlSectionsFromCarePlan.length
      ? pbsAdlSectionsFromCarePlan
      : pbsAdlSectionsFromGlobal) || []

  const defaultPBSSections = getDefaultPBSSections({
    carePlanAdl: {
      ...(fields as any)?.carePlanAdl,
      sections: pbsAdlSections
    }
  } as CarePlan)

  const pbsSectionsForForm = hasInitialPBSSections
    ? (fields.sections as any)
    : (defaultPBSSections as any)

  const getFluidTrackingDefaults = () => {
    const backendFluidTracking = (initial as any)?.fluidTracking
    return {
      enabled: backendFluidTracking?.enabled || false,
      intakeTarget: backendFluidTracking?.intakeTarget || ''
    }
  }

  const fluidTrackingDefaults = getFluidTrackingDefaults()

  if (!fields || !carers || !documents || !resident || !router) {
    return <Spinner />
  }

  return (
    <div style={styles.wrapper}>
      <NavigationModal canNavigate={canNavigate} />
      <Header
        fields={fields as ICarePlanForm}
        carers={carers}
        errors={errors || []}
        signatureLogs={signatureLogs}
        resident={resident}
        setFormField={setFormField}
        carePlanNominative={carePlanNominative}
        handleCancel={handleCancel}
        FORM_ID={FORM_ID}
      />
      {isPBSAssessment && (
        <CarePlanSectionContainer
          sections={
            hasInitialPBSSections ? fields?.sections : defaultPBSSections
          }
          form={formPBS}
        />
      )}
      {!isPBSAssessment && (
        <Section
          fields={fields}
          title="Strengths"
          FORM_ID={FORM_ID}
          content={fields.strengths}
        />
      )}
      {!isPBSAssessment && (
        <Section
          fields={fields}
          FORM_ID={FORM_ID}
          title="Needs"
          content={fields.needs}
          innerComponent={
            features?.features?.skillsAndNeeds?.enabled &&
            profileNeedOptions?.length > 0 ? (
              <AntdForm form={form}>
                <div style={styles.needItem}>
                  <InputSelect
                    testId="needs-select"
                    type="skillNeeds"
                    label={t(carePlansConstants.SELECT_NEEDS)}
                    layout="row"
                    placeholder={t(carePlansConstants.CHOOSE_FORM_LIST)}
                    groupedOptions={profileNeedOptions?.map((needOption) => ({
                      label: needOption.name,
                      options:
                        needOption.needs?.map((need) => ({
                          label: need.name,
                          value: need.id
                        })) ?? []
                    }))}
                    mode="multiple"
                    style={styles.inputSelect}
                    formItemstyle={styles.formInputSelectWrapper}
                    onChange={(value) =>
                      setFormField({ key: 'skillNeeds', value })
                    }
                  />
                </div>
              </AntdForm>
            ) : (
              <div style={{ paddingTop: 17, marginLeft: 24 }}>
                {textNode}
                {linkNode}
              </div>
            )
          }
        />
      )}
      <RiskAssessmentsSection
        carers={carers}
        fields={fields}
        riskAssessments={riskAssessments}
        router={router}
        setFormField={setFormField}
        onOpenModal={openRiskAssessmentModal}
        showToast={showToast}
      />
      {hasGoals && (
        <CarePlanGoalsListInput
          onChange={(value) =>
            setFormField({
              key: 'goals',
              value
            })
          }
          disabled={false}
          value={(fields.goals as string[]) || []}
        />
      )}
      {displayFluidTracking && (
        <div style={styles.sectionContainer}>
          <FluidTrackingSection
            isEnabled={
              fields.fluidTrackingEnabled ?? fluidTrackingDefaults.enabled
            }
            intakeTarget={
              fields.fluidTrackingIntakeTarget ??
              fluidTrackingDefaults.intakeTarget
            }
            onEnabledChange={(enabled) =>
              setFormField({ key: 'fluidTrackingEnabled', value: enabled })
            }
            onIntakeTargetChange={(target) =>
              setFormField({ key: 'fluidTrackingIntakeTarget', value: target })
            }
            showValidationError={showFluidTrackingValidation}
          />
        </div>
      )}
      <div style={styles.sectionContainer}>
        <CarePlanDocuments
          carePlan={fields}
          documents={documents}
          onCreate={() => openDocumentModal({ type: 'create' })}
          onEdit={(document: any) =>
            openDocumentModal({ document, type: 'edit' })
          }
          onDelete={handleDeleteDocument}
        />
      </div>
      {informedConsentFlagEnabled && (
        <ConsentSection
          residentId={id}
          value={
            fields.consentFormIds ||
            fields.consentForms?.map((form) => form.id) ||
            []
          }
          onChange={(selectedIds) =>
            setFormField({
              key: 'consentFormIds',
              value: selectedIds
            })
          }
        />
      )}
      <NextReview
        fields={fields}
        FORM_ID={FORM_ID}
        setFormField={setFormField}
      />
      <SignatureSection
        carePlan={fields}
        logs={signatureLogs || fields.logs}
        resident={resident}
        setLoadedListItem={setLoadedListItem}
        preferredCarePlanName={carePlanNominative}
        item={signatureItem?.id}
        category={(signatureItem as LogCategoryItem)?.category as string}
        informedConsentEnabled={informedConsentFlagEnabled}
      />
      <Footer
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}

const FORM_ID = 'editCarePlan'
const DOCUMENT_MODAL_ID = 'carePlanDocument'
const RISK_ASSESSMENT_MODAL_ID = 'createRiskAssessment'

const mapStateToProps = (state: any, props: any) => {
  const { id } = props.params

  // Documents loaded globally for this resident (may be empty on fresh load)
  const residentDocuments =
    _.filter(state.data.documents ?? [], { resident: id }) ?? []

  // Populated documents already present on the care plan form (from /populated)
  const carePlanForm = state.forms?.editCarePlan
  const populatedDocuments = carePlanForm?.fields?.populatedDocuments ?? []

  // Merge both sources and de-duplicate by document id, preferring updated resident docs
  const documents = _.uniqBy(
    [...(residentDocuments || []), ...(populatedDocuments || [])],
    'id'
  ).sort((a, b) => (a.id || '').localeCompare(b.id || ''))

  return {
    signatureItem: _.find(state.data['log-category-items'], {
      itemId: 'signature'
    }),
    signatureLogs: Selectors.getCarePlanLogs(state, props),
    riskAssessments: _.filter(state.data['risk-assessments'], { resident: id }),
    carers: state.data.carers,
    documents,
    userId: state.global.user.id,
    resident: _.find(state.data.residents, { id }),
    carePlanNominative: state.global.home.preferredCarePlansNominative,
    profileNeedOptions: state.data['skill-need-templates/categories'],
    adls: state.carePlans.adls,
    features: state.global.home,
    goals: state.forms?.editCarePlan?.fields?.goals
  }
}

const mapDispatchToProps = (dispatch: any, props: any) => ({
  deleteDocument: (id: unknown, onComplete: any) =>
    dispatch(
      Operations.deleteFormItem({
        id,
        list: 'documents',
        onComplete
      })
    ),
  setLoadedListItem: (listId: unknown, item: unknown) =>
    dispatch(Actions.setLoadedListItem(listId as string, item as object)),
  // @ts-ignore
  openDocumentModal: ({ document, type }) => {
    dispatch(
      Actions.openModal({
        modalId: DOCUMENT_MODAL_ID,
        data: { type, parentFormId: FORM_ID }
      })
    )
    dispatch(
      Actions.addForm({
        fields: { resident: props.router.params.id } as any,
        formId: DOCUMENT_MODAL_ID,
        initial: document
      })
    )
  },
  openRiskAssessmentModal: (completedRiskAssessment: any) => {
    const hasFullFormData =
      completedRiskAssessment?.form?.id &&
      !completedRiskAssessment?.form?.id?.startsWith?.('undefined')
    dispatch(
      Actions.openModal({
        modalId: RISK_ASSESSMENT_MODAL_ID,
        data: {
          completedRiskAssessment: hasFullFormData
            ? completedRiskAssessment
            : undefined,
          riskAssessmentId: !hasFullFormData
            ? completedRiskAssessment?.id
            : undefined,
          parentFormId: FORM_ID,
          attachToCarePlan: props.fields
        }
      })
    )
  },
  fetchNeedsOptions: () =>
    dispatch(
      Operations.loadList('skill-need-templates/categories', { needs: 1 })
    ),
  removeForm: () => dispatch(Actions.removeForm(FORM_ID))
})

const wrappedForm = withRouter(
  // @ts-ignore
  connect(mapStateToProps, mapDispatchToProps)(CarePlanReviewForm)
)

export default Form(wrappedForm, FORM_ID)
